// AdminLockFunnelPage — F9.7 FASE 3 · plan-gating lock-funnel analytics.
//
// Reads from GET /api/admin/analytics/lock-funnel?window={1h|24h|7d|30d}
// and renders a 3-metric header + sortable table of (module, from_group)
// hover vs cta_click vs CTR.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TrendingUp, MousePointerClick, Hand, Loader2, RefreshCw } from "lucide-react";

import Header from "@/components/Header";
import { apiFetch } from "@/lib/api";

const WINDOWS = ["1h", "24h", "7d", "30d"];
const AUTOREFRESH_MS = 30000;

export default function AdminLockFunnelPage() {
    const [window, setWindow] = useState("7d");
    const [data, setData] = useState({
        window: "7d",
        total_hovers: 0,
        total_cta_clicks: 0,
        overall_ctr_pct: 0,
        by_module: [],
    });
    const [loading, setLoading] = useState(true);
    const [sortBy, setSortBy] = useState("ctr_pct");
    const [sortDir, setSortDir] = useState("desc");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch(`/admin/analytics/lock-funnel?window=${window}`);
            setData(res);
        } catch (_e) {
            // Silent fail — first mount may 401 before cookie propagates;
            // the 30s auto-refresh will recover.
        } finally {
            setLoading(false);
        }
    }, [window]);

    useEffect(() => { load(); }, [load]);
    // Auto-refresh every 30s
    useEffect(() => {
        const t = setInterval(() => load(), AUTOREFRESH_MS);
        return () => clearInterval(t);
    }, [load]);

    const sortedRows = useMemo(() => {
        const copy = [...(data.by_module || [])];
        copy.sort((a, b) => {
            const av = a[sortBy] ?? 0;
            const bv = b[sortBy] ?? 0;
            return sortDir === "desc" ? bv - av : av - bv;
        });
        return copy;
    }, [data.by_module, sortBy, sortDir]);

    const toggleSort = (col) => {
        if (sortBy === col) setSortDir((d) => d === "desc" ? "asc" : "desc");
        else { setSortBy(col); setSortDir("desc"); }
    };

    const sortArrow = (col) => sortBy === col ? (sortDir === "desc" ? " ↓" : " ↑") : "";

    return (
        <div className="min-h-screen" style={{ backgroundColor: "#0A0A0A" }}>
            <Header />
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-lock-funnel-page">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <p className="text-[11px] uppercase tracking-widest text-[#CCCCCC]">Admin · Analytics</p>
                        <h1 className="text-2xl md:text-3xl font-semibold text-[#FFFFFF] mt-1">Lock funnel</h1>
                        <p className="text-sm text-[#CCCCCC] mt-1">
                            Quali locked items generano più interesse e conversioni verso Pricing.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="flex rounded-md border overflow-hidden" style={{ borderColor: "#1A1A1A" }}>
                            {WINDOWS.map((w) => (
                                <button
                                    key={w}
                                    onClick={() => setWindow(w)}
                                    data-testid={`admin-lock-funnel-window-${w}`}
                                    className="px-3 py-1.5 text-xs font-medium transition-colors"
                                    style={{
                                        backgroundColor: window === w ? "#FFFFFF" : "#111111",
                                        color: window === w ? "#FFFFFF" : "#CCCCCC",
                                    }}
                                >
                                    {w}
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={load}
                            disabled={loading}
                            data-testid="admin-lock-funnel-refresh"
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs border text-[#FFFFFF] hover:bg-white/[0.04]"
                            style={{ borderColor: "#1A1A1A", backgroundColor: "#111111" }}
                        >
                            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                        </button>
                    </div>
                </div>

                {/* Metric cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                    <MetricCard
                        testId="admin-lock-funnel-metric-hovers"
                        icon={<Hand className="w-4 h-4" />}
                        label="Hover totali"
                        value={data.total_hovers}
                        hint="Intent signal · tooltip aperti"
                    />
                    <MetricCard
                        testId="admin-lock-funnel-metric-clicks"
                        icon={<MousePointerClick className="w-4 h-4" />}
                        label="CTA click totali"
                        value={data.total_cta_clicks}
                        hint="Click su 'Sblocca Pro →'"
                    />
                    <MetricCard
                        testId="admin-lock-funnel-metric-ctr"
                        icon={<TrendingUp className="w-4 h-4" />}
                        label="CTR complessivo"
                        value={`${data.overall_ctr_pct.toFixed(2)}%`}
                        hint="click / hover"
                        valueColor="#FFFFFF"
                    />
                </div>

                {/* Table */}
                <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: "#111111", borderColor: "#1A1A1A" }}>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[11px] uppercase tracking-widest text-[#CCCCCC] border-b" style={{ borderColor: "#1A1A1A" }}>
                                <th className="px-4 py-3">Modulo</th>
                                <th className="px-4 py-3">Gruppo nav</th>
                                <SortTh onClick={() => toggleSort("hover_count")} label={`Hovers${sortArrow("hover_count")}`} />
                                <SortTh onClick={() => toggleSort("cta_click_count")} label={`CTA clicks${sortArrow("cta_click_count")}`} />
                                <SortTh onClick={() => toggleSort("unique_sessions_count")} label={`Sessioni uniche${sortArrow("unique_sessions_count")}`} />
                                <SortTh onClick={() => toggleSort("ctr_pct")} label={`CTR %${sortArrow("ctr_pct")}`} />
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={6} className="px-4 py-8 text-center text-[#CCCCCC]">
                                    <Loader2 className="w-5 h-5 animate-spin inline" />
                                </td></tr>
                            )}
                            {!loading && sortedRows.length === 0 && (
                                <tr><td colSpan={6} className="px-4 py-8 text-center text-[#CCCCCC]">
                                    Nessun lock event nel periodo. Carica la nav orizzontale e passa sopra items locked per generare dati.
                                </td></tr>
                            )}
                            {!loading && sortedRows.map((r) => (
                                <tr
                                    key={`${r.module}:${r.from_group}`}
                                    data-testid={`admin-lock-funnel-row-${r.module}-${r.from_group}`}
                                    className="border-b"
                                    style={{ borderColor: "#1A1A1A" }}
                                >
                                    <td className="px-4 py-3 text-[#FFFFFF] font-mono text-xs">{r.module}</td>
                                    <td className="px-4 py-3 text-[#CCCCCC] font-mono text-xs">{r.from_group}</td>
                                    <td className="px-4 py-3 tabular-nums text-[#FFFFFF]">{r.hover_count}</td>
                                    <td className="px-4 py-3 tabular-nums text-[#FFFFFF]">{r.cta_click_count}</td>
                                    <td className="px-4 py-3 tabular-nums text-[#CCCCCC]">{r.unique_sessions_count}</td>
                                    <td className="px-4 py-3 tabular-nums font-semibold" style={{ color: r.ctr_pct >= 10 ? "#CCCCCC" : r.ctr_pct >= 3 ? "#FFFFFF" : "#CCCCCC" }}>
                                        {Number(r.ctr_pct).toFixed(2)}%
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <p className="text-xs text-[#999999] mt-3">
                    Auto-refresh ogni 30s · finestra: {data.window}
                </p>
            </main>
        </div>
    );
}

function MetricCard({ icon, label, value, hint, valueColor, testId }) {
    return (
        <div
            data-testid={testId}
            className="rounded-xl border p-4"
            style={{ backgroundColor: "#111111", borderColor: "#1A1A1A" }}
        >
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[#CCCCCC]">
                {icon}{label}
            </div>
            <p className="mt-2 text-2xl font-semibold tabular-nums" style={{ color: valueColor || "#FFFFFF" }}>
                {value}
            </p>
            <p className="mt-1 text-xs text-[#999999]">{hint}</p>
        </div>
    );
}

function SortTh({ onClick, label }) {
    return (
        <th
            onClick={onClick}
            className="px-4 py-3 cursor-pointer hover:text-[#FFFFFF] select-none"
        >
            {label}
        </th>
    );
}
